import { throttle } from 'lodash';
import { useEffect, useRef } from 'react';
import { SCROLL_DEPTH_MILESTONES, getNewScrollMilestones } from 'utils/scrollDepth';

// The whole results portal scrolls inside this container, not the window
// (see ResponsiveLayout), so depth is measured against its scroll position.
const DEFAULT_CONTAINER_ID = 'appWrapper';

type UseScrollDepthOptions = {
  /** Called once for each milestone the user scrolls past (per `resetKey`). */
  onMilestone: (depth: number) => void;
  /** Re-arms tracking with a fresh set of fired milestones when it changes. */
  resetKey?: unknown;
  enabled?: boolean;
  /** id of the scrollable container element. Defaults to the app wrapper. */
  containerId?: string;
  /** Depth percentages to fire at. Defaults to 25 / 50 / 75 / 100. */
  milestones?: readonly number[];
  throttleMs?: number;
};

/**
 * Generic scroll-depth tracking. Watches the scroll progress of a container and
 * fires `onMilestone(depth)` once per milestone crossed (0% at the top → 100% at
 * the very bottom); nothing fires on content short enough not to scroll. The
 * caller decides what to do with each milestone (e.g. send an analytics event).
 */
export const useScrollDepth = ({
  onMilestone,
  resetKey,
  enabled = true,
  containerId = DEFAULT_CONTAINER_ID,
  milestones = SCROLL_DEPTH_MILESTONES,
  throttleMs = 200,
}: UseScrollDepthOptions) => {
  // Keep the latest callback without re-attaching the scroll listener.
  const onMilestoneRef = useRef(onMilestone);
  onMilestoneRef.current = onMilestone;

  useEffect(() => {
    if (!enabled) return;
    const el = document.getElementById(containerId);
    if (!el) return;

    const fired = new Set<number>();

    const onScroll = throttle(
      () => {
        const scrollable = el.scrollHeight - el.clientHeight;
        if (scrollable <= 0) return; // container doesn't scroll — fire nothing
        const percent = (el.scrollTop / scrollable) * 100;
        for (const milestone of getNewScrollMilestones(percent, fired, milestones)) {
          fired.add(milestone);
          onMilestoneRef.current(milestone);
        }
      },
      throttleMs,
      { leading: false, trailing: true },
    );

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      onScroll.cancel();
    };
  }, [enabled, containerId, throttleMs, milestones, resetKey]);
};
